/**
 * Swiper init/destroy for carousels.
 * Load after Swiper bundle. Runs on DOMContentLoaded and on shopify:section:load for theme editor.
 */
(function () {
  if (typeof Swiper === 'undefined') return;

  var DESKTOP_BREAKPOINT = 750;
  var MOBILE_PEEK_OFFSET = 0.12;
  var SWIPER_DISABLED_CLASS = 'swiper-button-disabled';
  // Slide fade is instant when the user prefers reduced motion.
  var REDUCED_MOTION = Boolean(
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  var HERO_FADE_MS = REDUCED_MOTION ? 0 : 800;

  function parsePositiveInteger(value, fallbackValue) {
    var parsedValue = parseInt(value, 10);
    if (!Number.isFinite(parsedValue) || parsedValue < 1) {
      return fallbackValue;
    }

    return parsedValue;
  }

  function parseBooleanAttribute(el, attributeName) {
    return el.getAttribute(attributeName) === 'true';
  }

  function markSwiperSlidesWithVideo(el) {
    if (!el || !el.querySelectorAll) {
      return;
    }

    var slides = el.querySelectorAll('.swiper-slide');
    for (var i = 0; i < slides.length; i += 1) {
      slides[i].classList.toggle(
        'swiper-slide--has-video',
        Boolean(slides[i].querySelector('video.slide__video'))
      );
    }
  }

  function ensureSwiperSlideClasses(el) {
    var wrapper = el.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return;
    }

    var children = wrapper.children;
    for (var i = 0; i < children.length; i += 1) {
      var child = children[i];
      if (child.classList.contains('swiper-slide')) {
        continue;
      }

      var slideWrapper = document.createElement('div');
      slideWrapper.className = 'swiper-slide';
      slideWrapper.setAttribute('data-bon-swiper-slide-wrapper', 'true');

      wrapper.insertBefore(slideWrapper, child);
      slideWrapper.appendChild(child);
    }
  }

  function getNavigationButtons(wrapper, sliderId) {
    if (!wrapper || !sliderId) {
      return null;
    }

    var prevButton = wrapper.querySelector('[data-carousel-target="' + sliderId + '"][data-carousel-direction="prev"]');
    var nextButton = wrapper.querySelector('[data-carousel-target="' + sliderId + '"][data-carousel-direction="next"]');

    if (!prevButton || !nextButton) {
      return null;
    }

    return {
      prev: prevButton,
      next: nextButton
    };
  }

  function getProgressFillElements(wrapper, sliderId) {
    if (!wrapper || !sliderId) {
      return [];
    }

    return Array.prototype.slice.call(
      wrapper.querySelectorAll('[data-carousel-progress-fill="' + sliderId + '"]')
    );
  }

  function getAutoplayToggleButton(wrapper, sliderId) {
    if (!wrapper || !sliderId) {
      return null;
    }

    return wrapper.querySelector('[data-carousel-autoplay-toggle="' + sliderId + '"]');
  }

  function setAutoplayToggleState(button, isPlaying) {
    if (!button) {
      return;
    }

    var playLabel = button.getAttribute('data-label-play') || 'Play';
    var pauseLabel = button.getAttribute('data-label-pause') || 'Pause';

    button.classList.toggle('is-paused', !isPlaying);
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  }

  function getHeroActiveSlide(swiper) {
    if (!swiper || !swiper.el) {
      return null;
    }

    return swiper.el.querySelector('.swiper-slide-active')
      || (swiper.slides && swiper.slides[swiper.activeIndex])
      || null;
  }

  function getHeroSlideVideos(root) {
    if (!root || !root.querySelectorAll) {
      return [];
    }

    return Array.prototype.slice.call(root.querySelectorAll('video.slide__video'));
  }

  function isHeroVideoVisible(video) {
    var media = video.closest('.slide__image-media');
    if (!media) {
      return false;
    }

    var isMobileSlot = media.classList.contains('slide__image-media--mobile');
    var isDesktopSlot = media.classList.contains('slide__image-media--desktop');
    if (!isMobileSlot && !isDesktopSlot) {
      return true;
    }

    var hasMobileOverride = Boolean(media.closest('.slide__image-container--has-mobile-media'));
    if (!hasMobileOverride) {
      return !isMobileSlot;
    }

    var isMobileViewport = window.matchMedia('(max-width: ' + (DESKTOP_BREAKPOINT - 1) + 'px)').matches;

    return isMobileViewport ? isMobileSlot : isDesktopSlot;
  }

  function markHeroVideoPoster(video, ready) {
    var media = video.closest('.slide__image-media');
    if (!media) {
      return;
    }

    media.classList.toggle('slide__image-media--video-ready', Boolean(ready));
  }

  function bindHeroVideoPosterEvents(video) {
    if (video.bonHeroPosterBound) {
      return;
    }

    video.bonHeroPosterBound = true;
    video.addEventListener('playing', function () {
      markHeroVideoPoster(video, true);
    });
    video.addEventListener('pause', function () {
      markHeroVideoPoster(video, video.currentTime > 0 && video.readyState >= 2);
    });

    if (!video.paused && video.readyState >= 2) {
      markHeroVideoPoster(video, true);
    }
  }

  function ensureHeroVideoInline(video) {
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    bindHeroVideoPosterEvents(video);
  }

  function rewindHeroVideo(video) {
    ensureHeroVideoInline(video);

    if (video.currentTime > 0) {
      try {
        video.currentTime = 0;
      } catch (error) {
        // Safari can reject currentTime before metadata is ready.
      }
    }

    markHeroVideoPoster(video, false);
  }

  function pauseHeroVideo(video, resetToStart) {
    ensureHeroVideoInline(video);
    video.pause();

    if (resetToStart) {
      rewindHeroVideo(video);

      return;
    }

    // Keep a painted frame (paused mid-playback). Show the poster when
    // there is nothing to freeze — iOS often blanks currentTime 0.
    markHeroVideoPoster(video, video.currentTime > 0 && video.readyState >= 2);
  }

  function playHeroVideo(video, fromStart) {
    ensureHeroVideoInline(video);

    if (fromStart) {
      rewindHeroVideo(video);
    }

    if (!video.paused) {
      markHeroVideoPoster(video, video.readyState >= 2);

      return;
    }

    var playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(function () {
        markHeroVideoPoster(video, true);
      }).catch(function () {
        markHeroVideoPoster(video, false);
      });

      return;
    }

    markHeroVideoPoster(video, video.readyState >= 2);
  }

  function forEachHeroSlideVideo(swiper, callback) {
    if (!swiper || !swiper.el) {
      return;
    }

    var activeSlide = getHeroActiveSlide(swiper);
    var videos = getHeroSlideVideos(swiper.el);

    for (var i = 0; i < videos.length; i += 1) {
      var video = videos[i];
      var onActive = Boolean(
        activeSlide && activeSlide.contains(video) && isHeroVideoVisible(video)
      );
      callback(video, onActive);
    }
  }

  function pauseHeroSlideVideos(swiper, rewindActive, rewindInactive) {
    forEachHeroSlideVideo(swiper, function (video, onActive) {
      pauseHeroVideo(video, onActive ? rewindActive : rewindInactive);
    });
  }

  function rewindOffscreenHeroVideos(swiper) {
    forEachHeroSlideVideo(swiper, function (video, onActive) {
      if (!onActive) {
        pauseHeroVideo(video, true);
      }
    });
  }

  function playActiveHeroVideo(swiper, fromStart) {
    forEachHeroSlideVideo(swiper, function (video, onActive) {
      if (onActive) {
        playHeroVideo(video, fromStart);
      } else {
        pauseHeroVideo(video, false);
      }
    });
  }

  function getSegmentLength(fill, fallbackValue) {
    var parsedValue = parseFloat(fill.getAttribute('data-seg-len') || '');
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return fallbackValue;
    }

    return parsedValue;
  }

  function resetProgressRings(fills) {
    for (var i = 0; i < fills.length; i += 1) {
      snapSegmentEmpty(fills[i]);
    }
  }

  function getActiveSegmentIndex(swiper, segmentCount) {
    if (!swiper || segmentCount < 1) {
      return 0;
    }

    var realIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;

    return ((realIndex % segmentCount) + segmentCount) % segmentCount;
  }

  function updateActiveSegmentProgress(swiper, fills, progressLeft, activeIndexOverride) {
    if (fills.length < 1) {
      return;
    }

    var activeIndex = typeof activeIndexOverride === 'number'
      ? ((activeIndexOverride % fills.length) + fills.length) % fills.length
      : getActiveSegmentIndex(swiper, fills.length);
    var activeFill = fills[activeIndex];
    if (!activeFill) {
      return;
    }

    cancelSegmentTween(activeFill);
    writeSegmentOffset(activeFill, (getSegmentLength(activeFill, 0) * progressLeft).toFixed(1));
  }

  function writeSegmentOffset(fill, offsetValue) {
    fill.bonSegmentOffset = Number(offsetValue);
    fill.style.strokeDashoffset = offsetValue;
  }

  function cancelSegmentTween(fill) {
    if (fill.bonSegmentTween) {
      cancelAnimationFrame(fill.bonSegmentTween);
      fill.bonSegmentTween = null;
    }
  }

  function snapSegment(fill, offsetValue) {
    if (!fill) {
      return;
    }

    cancelSegmentTween(fill);
    writeSegmentOffset(fill, offsetValue);
  }

  function snapSegmentFull(fill) {
    snapSegment(fill, 0);
  }

  function snapSegmentEmpty(fill) {
    snapSegment(fill, getSegmentLength(fill, 0));
  }

  // Stories-style fill: past slides full, active + upcoming empty.
  // Rebuild from the active index so a missed reverse/swipe cannot leave
  // leftover stroke on another arc. Autoplay ticks then sweep only active.
  function paintStoriesProgress(fills, activeIndex) {
    if (!fills || fills.length < 1) {
      return;
    }

    var count = fills.length;
    var index = ((activeIndex % count) + count) % count;
    if (!Number.isFinite(index)) {
      index = 0;
    }

    for (var i = 0; i < count; i += 1) {
      if (i < index) {
        snapSegmentFull(fills[i]);
      } else {
        snapSegmentEmpty(fills[i]);
      }
    }
  }

  function isHeroSwipeIgnoredTarget(target) {
    if (!target || !target.closest) {
      return true;
    }

    return Boolean(
      target.closest('a, button, input, textarea, select, .bon-carousel__hero-controls')
    );
  }

  // Axis-locked pointer swipe → onSwipe(±1). Vertical intent never preventDefault.
  // iOS: commit on pointercancel when already locked to x; do not capture touch.
  function bindHeroSwipe(sliderEl, onSwipe) {
    if (!sliderEl || typeof onSwipe !== 'function') {
      return;
    }

    if (sliderEl.bonHeroSwipeAbort) {
      sliderEl.bonHeroSwipeAbort.abort();
    }

    var abort = new AbortController();
    sliderEl.bonHeroSwipeAbort = abort;
    var signal = abort.signal;

    var AXIS_RATIO = 1.5;
    var AXIS_SLOP = 12;
    var TOUCH_DISTANCE = 40;
    var MOUSE_DISTANCE = 56;
    var POINTER_COOLDOWN_MS = 280;
    var pointerSwipe = null;
    var gestureBusyUntil = 0;
    var suppressClickUntil = 0;

    function commitPointerSwipe(state) {
      if (!state || state.locked !== 'x') {
        return;
      }

      var dx = state.lastX - state.startX;
      var threshold = state.pointerType === 'mouse' ? MOUSE_DISTANCE : TOUCH_DISTANCE;
      if (Math.abs(dx) < threshold) {
        return;
      }

      var now = performance.now();
      if (now < gestureBusyUntil) {
        return;
      }

      gestureBusyUntil = now + POINTER_COOLDOWN_MS;
      suppressClickUntil = now + POINTER_COOLDOWN_MS;
      onSwipe(dx < 0 ? 1 : -1);
    }

    sliderEl.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      if (isHeroSwipeIgnoredTarget(event.target)) {
        return;
      }

      pointerSwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        locked: null,
        pointerType: event.pointerType || 'mouse'
      };
    }, { signal: signal });

    sliderEl.addEventListener('pointermove', function (event) {
      var state = pointerSwipe;
      if (!state || state.pointerId !== event.pointerId || state.locked === 'y') {
        return;
      }

      state.lastX = event.clientX;
      state.lastY = event.clientY;

      var dx = event.clientX - state.startX;
      var dy = event.clientY - state.startY;
      var absX = Math.abs(dx);
      var absY = Math.abs(dy);

      if (!state.locked) {
        if (absX < AXIS_SLOP && absY < AXIS_SLOP) {
          return;
        }

        if (absY * AXIS_RATIO >= absX) {
          state.locked = 'y';
          return;
        }

        if (absX >= absY * AXIS_RATIO) {
          state.locked = 'x';
          if (state.pointerType === 'mouse' && sliderEl.setPointerCapture) {
            try {
              sliderEl.setPointerCapture(event.pointerId);
            } catch (_) {
              /* capture optional */
            }
          }
        } else {
          return;
        }
      }

      if (state.locked === 'x' && event.cancelable) {
        event.preventDefault();
      }
    }, { signal: signal, passive: false });

    function endPointerSwipe(event) {
      var state = pointerSwipe;
      if (!state || state.pointerId !== event.pointerId) {
        return;
      }

      state.lastX = event.clientX;
      state.lastY = event.clientY;
      commitPointerSwipe(state);
      pointerSwipe = null;
    }

    function cancelPointerSwipe(event) {
      var state = pointerSwipe;
      if (!state || (event.pointerId != null && state.pointerId !== event.pointerId)) {
        pointerSwipe = null;
        return;
      }

      commitPointerSwipe(state);
      pointerSwipe = null;
    }

    sliderEl.addEventListener('pointerup', endPointerSwipe, { signal: signal });
    sliderEl.addEventListener('pointercancel', cancelPointerSwipe, { signal: signal });
    sliderEl.addEventListener('click', function (event) {
      if (performance.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { signal: signal, capture: true });
  }

  function syncSlideshowSlideVisibility(swiper) {
    if (!swiper || !swiper.slides) {
      return;
    }

    for (var i = 0; i < swiper.slides.length; i += 1) {
      var slideRoot = swiper.slides[i];
      var slideshowSlide = slideRoot.matches('slideshow-slide') ? slideRoot : slideRoot.querySelector('slideshow-slide');
      if (!slideshowSlide) {
        continue;
      }

      // These slides are controlled by Swiper (not slideshow-component).
      // Keep them visible so fade/crossfade transitions do not blank out.
      slideshowSlide.setAttribute('aria-hidden', 'false');
    }
  }

  function createDeviceSettings(isInfiniteCenter, slidesPerView) {
    return {
      isInfiniteCenter: isInfiniteCenter,
      slidesPerView: slidesPerView,
      centeredSlides: isInfiniteCenter,
      centeredSlidesBounds: !isInfiniteCenter
    };
  }

  /**
   * Horizontal visibility of a slide inside the swiper viewport.
   * Used to avoid slideTo on clicks when the slide is already mostly on screen.
   */
  function getSlideVisibilityRatio(swiper, slideEl) {
    if (!swiper || !swiper.el || !slideEl) {
      return 0;
    }

    var containerRect = swiper.el.getBoundingClientRect();
    var slideRect = slideEl.getBoundingClientRect();
    var slideWidth = slideRect.width;

    if (slideWidth <= 0) {
      return 0;
    }

    var visibleLeft = Math.max(slideRect.left, containerRect.left);
    var visibleRight = Math.min(slideRect.right, containerRect.right);
    var visibleWidth = Math.max(0, visibleRight - visibleLeft);

    return visibleWidth / slideWidth;
  }

  function isSlideMostlyVisible(swiper, slideEl) {
    return getSlideVisibilityRatio(swiper, slideEl) >= 0.5;
  }

  function buildSwiperOptions(el, navigationButtons, mobileSettings, desktopSettings) {
    var shouldCarryPlaybackToNextSlide = false;

    function carryPlaybackToActiveSlide(swiperInstance) {
      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        shouldCarryPlaybackToNextSlide = false;
        return;
      }

      if (!shouldCarryPlaybackToNextSlide) {
        return;
      }

      shouldCarryPlaybackToNextSlide = false;

      var activeSlide = swiperInstance.slides && swiperInstance.slides[swiperInstance.activeIndex];
      if (!activeSlide) {
        return;
      }

      var activeVideoCard = activeSlide.matches('video-card') ? activeSlide : activeSlide.querySelector('video-card');
      if (!activeVideoCard || activeVideoCard.classList.contains('is-playing')) {
        return;
      }

      var playButton = activeVideoCard.querySelector('[data-bon-play-button]');
      if (playButton) {
        playButton.click();
      }
    }

    /** @type {any} */
    var swiperOptions = {
      loop: mobileSettings.isInfiniteCenter,
      rewind: false,
      watchOverflow: false,
      slidesPerView: mobileSettings.slidesPerView,
      slidesPerGroup: 1,
      // Only advance on click when the target slide is mostly out of view
      // (see click handler). Avoids shifting when playing an already-visible video.
      slideToClickedSlide: false,
      spaceBetween: 16,
      centeredSlides: mobileSettings.centeredSlides,
      centeredSlidesBounds: mobileSettings.centeredSlidesBounds,
      updateOnWindowResize: true,
      resizeObserver: true,
      breakpoints: {
        750: {
          loop: desktopSettings.isInfiniteCenter,
          rewind: false,
          slidesPerView: desktopSettings.slidesPerView,
          centeredSlides: desktopSettings.centeredSlides,
          centeredSlidesBounds: desktopSettings.centeredSlidesBounds
        }
      },
      // Trackpads emit dozens of tiny wheel events per gesture. Without
      // thresholds, Swiper advances once on the first tick, then again after
      // the transition ends when inertia is still firing. Keep thresholdTime
      // moderate so follow-up swipes stay responsive without freeMode (which
      // made finger swipes feel throttled on touch devices).
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: true,
        thresholdDelta: 10,
        thresholdTime: 450
      },
      on: {
        click: function (swiper) {
          var clickedSlide = swiper.clickedSlide;
          if (!clickedSlide) {
            return;
          }

          if (isSlideMostlyVisible(swiper, clickedSlide)) {
            return;
          }

          // Reuse Swiper's loop-aware helper instead of raw slideTo/slideToLoop.
          if (typeof swiper.slideToClickedSlide === 'function') {
            swiper.slideToClickedSlide();
          }
        },
        slideChangeTransitionStart: function () {
          shouldCarryPlaybackToNextSlide = Boolean(el.querySelector('video-card.is-playing'));
        },
        slideChangeTransitionEnd: function () {
          carryPlaybackToActiveSlide(this);
        }
      }
    };

    if (navigationButtons) {
      swiperOptions.navigation = {
        prevEl: navigationButtons.prev,
        nextEl: navigationButtons.next,
        disabledClass: SWIPER_DISABLED_CLASS
      };
    }

    return swiperOptions;
  }

  function initCarousel(el) {
    if (el.offsetWidth === 0) return null;

    // Some block types render custom elements and might not include
    // `swiper-slide` in Liquid. Enforce it for all direct slide nodes.
    ensureSwiperSlideClasses(el);

    // parse hero-mode related attributes first because hero mode intentionally
    // renders without arrow buttons.
    var heroMode = parseBooleanAttribute(el, 'data-hero-mode');
    var heroSlideCount = parsePositiveInteger(el.getAttribute('data-slide-count') || '', 2);

    // Single-slide heroes render no controls and need no Swiper.
    if (heroMode && heroSlideCount <= 1) {
      return null;
    }

    var wrapper = el.closest('.bon-carousel');
    var navigationButtons = getNavigationButtons(wrapper, el.id);
    var progressFills = getProgressFillElements(wrapper, el.id);
    var autoplayToggleButton = getAutoplayToggleButton(wrapper, el.id);
    if (!navigationButtons && !heroMode) return null;

    var heroAutoplay = parseBooleanAttribute(el, 'data-autoplay');
    // delay is stored in seconds in the schema, convert to ms for Swiper
    var heroAutoplayDelaySeconds = parsePositiveInteger(el.getAttribute('data-autoplay-delay') || '', 3);
    var heroAutoplayDelay = heroAutoplayDelaySeconds * 1000;
    // Fade overlaps the start of the next ring. Add it to the delay so
    // settled dwell stays the merchant setting (reduced-motion fade is 0).
    var heroAutoplayCycleMs = heroAutoplayDelay + HERO_FADE_MS;
    // Pause/resume bookkeeping: ticks store remaining ms of the full cycle
    // so resume can keep the same remaining time (not a 0–1 of a shortened
    // delay, which jumps the ring when start() and play() share a frame).
    var lastTimeLeftMs = heroAutoplayCycleMs;
    var pausedTimeLeftMs = heroAutoplayCycleMs;
    // Early prev/next during an 800ms fade: track the intended index so each
    // click steps once from the last request, not Swiper's in-flight
    // activeIndex. heroNavTarget ignores stale slideChange from an
    // interrupted fade so autoplay can still sync when it is null.
    var heroIntentIndex = 0;
    var heroNavTarget = null;
    // Arrow stop()+start() briefly sets autoplay.running false. Pause videos
    // only when the user hit the progress toggle, not during that restart.
    var heroVideosPausedByUser = false;
    // stop()+start() to reschedule a full delay also emits autoplayStop/Start.
    // Skip toggling the play/pause button while that restart is in flight.
    var heroRestartingAutoplay = false;

    function restartHeroAutoplayTimer(swiperInstance) {
      if (
        heroVideosPausedByUser
        || !swiperInstance
        || !swiperInstance.autoplay
      ) {
        return;
      }

      heroRestartingAutoplay = true;

      if (swiperInstance.autoplay.running) {
        swiperInstance.autoplay.stop();
      }

      swiperInstance.autoplay.start();
      heroRestartingAutoplay = false;
    }

    function beginHeroSlideCycle(changedSwiper) {
      if (changedSwiper && changedSwiper.params && changedSwiper.params.autoplay) {
        changedSwiper.params.autoplay.delay = heroAutoplayCycleMs;
      }

      lastTimeLeftMs = heroAutoplayCycleMs;
      pausedTimeLeftMs = heroAutoplayCycleMs;
      restartHeroAutoplayTimer(changedSwiper);
    }

    var mobileColumns = parsePositiveInteger(el.getAttribute('data-columns-mobile') || '1', 1);
    var desktopColumns = parsePositiveInteger(el.getAttribute('data-columns-desktop') || '3', 3);
    var isInfiniteCenterMobile = parseBooleanAttribute(el, 'data-infinite-center-mobile');
    var isInfiniteCenterDesktop = parseBooleanAttribute(el, 'data-infinite-center-desktop');

    // hero mode forces a single-column, full-width layout
    if (heroMode) {
      mobileColumns = 1;
      desktopColumns = 1;
      // in hero mode we always loop/center so the fade effect can continue
      isInfiniteCenterDesktop = true;
      isInfiniteCenterMobile = true;
    }

    var mobileSettings = createDeviceSettings(true, mobileColumns + (heroMode ? 0 : MOBILE_PEEK_OFFSET));
    var desktopSettings = createDeviceSettings(isInfiniteCenterDesktop, desktopColumns);
    var swiperOptions = buildSwiperOptions(
      el,
      navigationButtons,
      mobileSettings,
      desktopSettings
    );

    // apply hero-specific Swiper overrides
    if (heroMode) {
      // In bundle builds these modules are already available. In modular
      // builds, add only modules that are actually exposed on Swiper.
      var fadeModule = Swiper.EffectFade;
      var autoplayModule = Swiper.Autoplay;
      if (fadeModule || autoplayModule) {
        swiperOptions.modules = swiperOptions.modules || [];
        if (fadeModule) swiperOptions.modules.push(fadeModule);
        if (autoplayModule) swiperOptions.modules.push(autoplayModule);
      }

      swiperOptions.autoplay = heroAutoplay
        ? {
          delay: heroAutoplayCycleMs,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          waitForTransition: false
        }
        : false;
      swiperOptions.effect = 'fade';
      swiperOptions.fadeEffect = { crossFade: true };
      swiperOptions.speed = HERO_FADE_MS;
      swiperOptions.navigation = false;
      // 2-slide fade + loop has no clones. Swiper loopFix then emits a second
      // slideChange (realIndex 0↔1 within ~10ms) and the next slide is skipped.
      // Rewind wraps last→first without that bounce. Breakpoints must not
      // re-enable loop (buildSwiperOptions sets it from infinite-center).
      swiperOptions.loop = false;
      swiperOptions.rewind = true;
      if (swiperOptions.breakpoints && swiperOptions.breakpoints[750]) {
        swiperOptions.breakpoints[750].loop = false;
        swiperOptions.breakpoints[750].rewind = true;
      }
      swiperOptions.preventInteractionOnTransition = false;
      // Swipes go through goHeroBy so a mid-fade step is not dropped.
      // Clicks on slide links still work.
      swiperOptions.allowTouchMove = false;
      swiperOptions.simulateTouch = false;
      swiperOptions.mousewheel = { enabled: false };

      var existingSlideChangeTransitionStart = swiperOptions.on.slideChangeTransitionStart;
      var existingSlideChangeTransitionEnd = swiperOptions.on.slideChangeTransitionEnd;
      swiperOptions.on.slideChangeTransitionStart = function () {
        if (existingSlideChangeTransitionStart) {
          existingSlideChangeTransitionStart.call(this);
        }

        var changedSwiper = this;
        // Swiper schedules the next timeout before this hook, so a shortened
        // resume delay would leak into the next slide unless we stop+start
        // after restoring the full cycle.
        if (heroAutoplay) {
          beginHeroSlideCycle(changedSwiper);
        }

        paintHeroRingFromSwiper(changedSwiper);
      };
      swiperOptions.on.slideChangeTransitionEnd = function () {
        if (existingSlideChangeTransitionEnd) {
          existingSlideChangeTransitionEnd.call(this);
        }

        paintHeroRingFromSwiper(this);
      };
      swiperOptions.on.slideResetTransitionEnd = function () {
        paintHeroRingFromSwiper(this);
      };

      if (heroAutoplay) {
        swiperOptions.on.autoplayTimeLeft = function (swiperInstance, timeLeft) {
          var remainingMs = timeLeft;
          if (remainingMs > heroAutoplayCycleMs) {
            remainingMs = heroAutoplayCycleMs;
          }
          if (remainingMs < 0) {
            remainingMs = 0;
          }
          // start() can emit one tick against the previous full delay before
          // the shortened resume delay sticks — that would rewind the ring.
          if (pausedTimeLeftMs < heroAutoplayCycleMs && remainingMs > pausedTimeLeftMs + 32) {
            remainingMs = pausedTimeLeftMs;
          }
          lastTimeLeftMs = remainingMs;
          updateActiveSegmentProgress(
            swiperInstance,
            progressFills,
            remainingMs / heroAutoplayCycleMs,
            heroNavTarget !== null ? heroIntentIndex : undefined
          );
        };
      } else {
        resetProgressRings(progressFills);
      }
    }

    function paintHeroRingFromSwiper(swiperInstance) {
      if (progressFills.length < 1 || !swiperInstance) {
        return;
      }

      paintStoriesProgress(
        progressFills,
        getActiveSegmentIndex(swiperInstance, progressFills.length)
      );
    }

    function paintHeroRingFromIndex(activeIndex) {
      paintStoriesProgress(progressFills, activeIndex);
    }

    var swiper = new Swiper(el, swiperOptions);
    if (heroMode) {
      markSwiperSlidesWithVideo(el);
    }
    // start autoplay manually as some versions require trigger
    if (heroMode && heroAutoplay && swiper.autoplay) {
      swiper.autoplay.start();
      playActiveHeroVideo(swiper);
    }

    if (heroMode) {
      heroIntentIndex = getActiveSegmentIndex(swiper, heroSlideCount);
      paintHeroRingFromSwiper(swiper);
      syncSlideshowSlideVisibility(swiper);

      swiper.on('slideChange', function () {
        syncSlideshowSlideVisibility(swiper);
        var liveIndex = getActiveSegmentIndex(swiper, heroSlideCount);
        if (heroNavTarget === null) {
          heroIntentIndex = liveIndex;
        } else if (liveIndex === heroNavTarget) {
          heroIntentIndex = liveIndex;
          heroNavTarget = null;
        }
      });

      swiper.on('slideChangeTransitionStart', function () {
        if (heroVideosPausedByUser) {
          pauseHeroSlideVideos(swiper, true, false);
        } else {
          playActiveHeroVideo(swiper, true);
        }
      });
      // Rewind off-slide clips after the fade so the outgoing video does
      // not flash its poster mid-crossfade.
      swiper.on('slideChangeTransitionEnd', function () {
        rewindOffscreenHeroVideos(swiper);

        if (heroVideosPausedByUser) {
          pauseHeroSlideVideos(swiper, false, true);
        } else {
          playActiveHeroVideo(swiper, false);
        }
      });
    }

    if (autoplayToggleButton) {
      setAutoplayToggleState(autoplayToggleButton, Boolean(swiper.autoplay && swiper.autoplay.running));

      if (autoplayToggleButton.bonCarouselAutoplayToggleHandler) {
        autoplayToggleButton.removeEventListener('click', autoplayToggleButton.bonCarouselAutoplayToggleHandler);
      }

      autoplayToggleButton.bonCarouselAutoplayToggleHandler = function (e) {
        e.preventDefault();

        if (!swiper.autoplay) {
          return;
        }

        if (swiper.autoplay.running) {
          heroVideosPausedByUser = true;
          pausedTimeLeftMs = lastTimeLeftMs;
          swiper.autoplay.stop();
          pauseHeroSlideVideos(swiper, false, true);
          setAutoplayToggleState(autoplayToggleButton, false);
        } else {
          // Resume the remaining ms of this slide. Start autoplay after
          // play() so media work does not eat the first ~200ms of the timer.
          // The slide-change hook restores the full delay afterwards.
          heroVideosPausedByUser = false;
          if (swiper.params && swiper.params.autoplay) {
            swiper.params.autoplay.delay = Math.max(1, Math.round(pausedTimeLeftMs));
          }
          updateActiveSegmentProgress(
            swiper,
            progressFills,
            pausedTimeLeftMs / heroAutoplayCycleMs
          );
          playActiveHeroVideo(swiper, false);
          swiper.autoplay.start();
          setAutoplayToggleState(autoplayToggleButton, true);
        }
      };

      autoplayToggleButton.addEventListener('click', autoplayToggleButton.bonCarouselAutoplayToggleHandler);

      swiper.on('autoplayStart', function () {
        if (heroRestartingAutoplay) {
          return;
        }

        setAutoplayToggleState(autoplayToggleButton, true);
      });

      swiper.on('autoplayStop', function () {
        if (heroRestartingAutoplay) {
          return;
        }

        setAutoplayToggleState(autoplayToggleButton, false);
      });
    }

    // ensure navigation buttons still work when loop is enabled, even if
    // swiper decides to disable them (tiny slide count, etc.)
    function intercept(e, method) {
      if (swiper.params.loop) {
        e.preventDefault();
        swiper[method]();
      }
    }

    function goHeroBy(delta) {
      if (heroSlideCount < 2) {
        return;
      }

      heroIntentIndex = ((heroIntentIndex + delta) % heroSlideCount + heroSlideCount) % heroSlideCount;
      heroNavTarget = heroIntentIndex;

      var liveIndex = getActiveSegmentIndex(swiper, heroSlideCount);
      // Interrupt the in-flight fade so the next request is not dropped.
      swiper.animating = false;

      var goToHeroIndex = function (speed) {
        swiper.slideTo(heroIntentIndex, speed, true, true);
      };

      if (liveIndex === heroIntentIndex) {
        // realIndex has not moved yet (or already matches). Snap so we do
        // not keep fading toward the slide the user just left.
        goToHeroIndex(0);
        paintHeroRingFromIndex(heroIntentIndex);
        heroNavTarget = null;
        swiper.animating = false;
        return;
      }

      goToHeroIndex(HERO_FADE_MS);
      paintHeroRingFromIndex(heroIntentIndex);
      if (HERO_FADE_MS === 0) {
        swiper.animating = false;
      }
    }

    if (navigationButtons) {
      if (navigationButtons.prev.bonCarouselLoopHandler) {
        navigationButtons.prev.removeEventListener('click', navigationButtons.prev.bonCarouselLoopHandler, true);
      }
      if (navigationButtons.next.bonCarouselLoopHandler) {
        navigationButtons.next.removeEventListener('click', navigationButtons.next.bonCarouselLoopHandler, true);
      }

      if (heroMode) {
        navigationButtons.prev.bonCarouselLoopHandler = function (e) {
          e.preventDefault();
          goHeroBy(-1);
        };
        navigationButtons.next.bonCarouselLoopHandler = function (e) {
          e.preventDefault();
          goHeroBy(1);
        };
      } else {
        navigationButtons.prev.bonCarouselLoopHandler = function (e) { intercept(e, 'slidePrev'); };
        navigationButtons.next.bonCarouselLoopHandler = function (e) { intercept(e, 'slideNext'); };
      }

      navigationButtons.prev.addEventListener('click', navigationButtons.prev.bonCarouselLoopHandler, true);
      navigationButtons.next.addEventListener('click', navigationButtons.next.bonCarouselLoopHandler, true);
    }

    // Prev/next and swipes kick off a fresh autoplay delay for the next slide.
    // Arrows resume a paused hero; swipes step without unpausing.
    function restartAutoplayDelay(resumeIfPaused) {
      var wasPausedByUser = heroVideosPausedByUser;

      if (resumeIfPaused !== false) {
        heroVideosPausedByUser = false;
      }

      beginHeroSlideCycle(swiper);

      if (heroVideosPausedByUser) {
        return;
      }

      playActiveHeroVideo(swiper, false);
      // stop()+start() swallows autoplayStart while heroRestartingAutoplay is
      // set, so a pause→arrow resume would leave the play icon up. Image
      // slides still run the dwell timer — show pause whenever arrows unpause.
      if (wasPausedByUser) {
        setAutoplayToggleState(autoplayToggleButton, true);
      }
      // Ring fill is painted from the intent/real index; do not reset here.
    }

    if (heroMode && navigationButtons) {
      if (navigationButtons.prev.bonCarouselRestartHandler) {
        navigationButtons.prev.removeEventListener('click', navigationButtons.prev.bonCarouselRestartHandler);
      }
      if (navigationButtons.next.bonCarouselRestartHandler) {
        navigationButtons.next.removeEventListener('click', navigationButtons.next.bonCarouselRestartHandler);
      }

      navigationButtons.prev.bonCarouselRestartHandler = restartAutoplayDelay;
      navigationButtons.next.bonCarouselRestartHandler = restartAutoplayDelay;

      navigationButtons.prev.addEventListener('click', restartAutoplayDelay);
      navigationButtons.next.addEventListener('click', restartAutoplayDelay);
    }

    if (heroMode) {
      bindHeroSwipe(el, function (delta) {
        goHeroBy(delta);
        restartAutoplayDelay(false);
      });
    }

    return swiper;
  }

  function destroyCarousel(el) {
    if (el.bonHeroSwipeAbort) {
      el.bonHeroSwipeAbort.abort();
      el.bonHeroSwipeAbort = null;
    }

    var existing = el.bonCarouselSwiper;
    if (!existing) {
      return;
    }

    existing.destroy(true, true);
    el.bonCarouselSwiper = null;
  }

  function runCarousels(trigger) {
    var sliders = document.querySelectorAll('.bon-carousel__slider');
    for (var i = 0; i < sliders.length; i += 1) {
      var el = sliders[i];
      // window.load used to destroy+reinit, which briefly restored full-width
      // slides (FOUC). Images only need a layout refresh — update in place.
      if (trigger === 'window.load' && el.bonCarouselSwiper) {
        el.bonCarouselSwiper.update();
        continue;
      }

      destroyCarousel(el);
      el.bonCarouselSwiper = initCarousel(el);
    }
  }

  function run(trigger) {
    runCarousels(trigger);
  }

  function initPendingCarousels() {
    var sliders = document.querySelectorAll('.bon-carousel__slider');
    for (var i = 0; i < sliders.length; i += 1) {
      var el = sliders[i];
      if (el.bonCarouselSwiper || el.offsetWidth === 0) {
        continue;
      }

      el.bonCarouselSwiper = initCarousel(el);
    }
  }

  function runAfterLayout(fn) {
    requestAnimationFrame(function () {
      requestAnimationFrame(fn);
    });
  }

  // Safari can stay on readyState=interactive while hero videos load, so
  // window.load never fires and rAF can be deferred. A timeout still runs.
  function boot(trigger) {
    runAfterLayout(function () { run(trigger + '+rAF'); });
    setTimeout(initPendingCarousels, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot('DOMContentLoaded');
      window.addEventListener('load', function () { run('window.load'); });
    });
  } else {
    boot('already-ready');
    window.addEventListener('load', function () { run('window.load'); });
  }


  document.addEventListener('shopify:section:load', function () {
    run();
  });
})();
